import { lazy, type ComponentType } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   A page that loads when it is first opened, not with the app.

   Every module's pages used to be in the one script the app starts with:
   2.3 MB for someone who only ever opens Now and Creators, parsed and
   compiled before the first screen (2026-09-29). Each module's pages are now
   their own chunk, fetched the first time one of them is opened and cached
   from then on.

   A deploy replaces every chunk's name. A window left open across one (the
   desktop app, mostly) asks for a chunk that is no longer there, and the
   page would fail to open. So a failed load reloads the app once, which
   brings the new names; a second failure within a minute is a real error
   and is shown as one rather than reloading for ever.
   ────────────────────────────────────────────────────────────────────────── */

const RELOADED_AT = "signet:chunk-reload";

function reloadOnce(error: unknown): never {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
  } catch {
    // Storage blocked: reload anyway, once per page load at most.
  }
  if (Date.now() - last > 60_000) {
    try {
      sessionStorage.setItem(RELOADED_AT, String(Date.now()));
    } catch {
      // As above.
    }
    window.location.reload();
  }
  throw error;
}

export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): ComponentType {
  return lazy(async () => {
    const loaded = await load().catch(reloadOnce);
    return { default: loaded[name] as unknown as ComponentType };
  });
}
